import { supabase } from '../config/supabase';

/**
 * Récupère les statistiques de visites des 7 derniers jours pour un événement
 * @param {string} eventId - L'ID de l'événement
 * @returns {Promise<Array<number>>} - Tableau avec le nombre de visites pour chaque jour (7 valeurs)
 */
export const getEventStats7Days = async (eventId) => {
  try {
    // Calculer la date d'il y a 7 jours
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    // Récupérer les visites des 7 derniers jours
    const { data, error } = await supabase
      .from('visits')
      .select('visited_at')
      .eq('event_id', eventId)
      .gte('visited_at', sevenDaysAgo.toISOString())
      .order('visited_at', { ascending: true });

    if (error) {
      console.error('[Stats] Erreur lors de la récupération des statistiques:', error);
      return [];
    }

    // Initialiser un tableau avec 7 zéros (un par jour)
    const stats = [0, 0, 0, 0, 0, 0, 0];
    
    // Compter les visites par jour
    data.forEach(visit => {
      const visitDate = new Date(visit.visited_at);
      const daysDiff = Math.floor((Date.now() - visitDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // daysDiff: 0 = aujourd'hui, 1 = hier, etc.
      // On inverse pour que l'index 0 soit il y a 7 jours et l'index 6 soit aujourd'hui
      const index = 6 - daysDiff;
      
      if (index >= 0 && index < 7) {
        stats[index]++;
      }
    });

    return stats;
  } catch (err) {
    console.error('[Stats] Exception lors de la récupération des statistiques:', err);
    return [];
  }
};

/**
 * Récupère les statistiques pour plusieurs événements en une seule fois
 * @param {Array<string>} eventIds - Liste des IDs d'événements
 * @returns {Promise<Object>} - Objet avec les statistiques par event_id
 */
export const getBulkEventStats7Days = async (eventIds) => {
  try {
    if (!eventIds || eventIds.length === 0) {
      return {};
    }

    // Calculer la date d'il y a 7 jours
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    // Récupérer toutes les visites en une seule requête
    const { data, error } = await supabase
      .from('visits')
      .select('event_id, visited_at')
      .in('event_id', eventIds)
      .gte('visited_at', sevenDaysAgo.toISOString())
      .order('visited_at', { ascending: true });

    if (error) {
      console.error('[Stats] Erreur lors de la récupération des statistiques:', error);
      return {};
    }

    // Initialiser les statistiques pour chaque événement
    const statsMap = {};
    eventIds.forEach(id => {
      statsMap[id] = [0, 0, 0, 0, 0, 0, 0];
    });

    // Compter les visites par jour pour chaque événement
    data.forEach(visit => {
      const visitDate = new Date(visit.visited_at);
      const daysDiff = Math.floor((Date.now() - visitDate.getTime()) / (1000 * 60 * 60 * 24));
      const index = 6 - daysDiff;
      
      if (index >= 0 && index < 7 && statsMap[visit.event_id]) {
        statsMap[visit.event_id][index]++;
      }
    });

    return statsMap;
  } catch (err) {
    console.error('[Stats] Exception lors de la récupération des statistiques:', err);
    return {};
  }
};
